import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useRequireApproved } from '../lib/auth';
import { useCreateChecklist, useWorkspace } from '../lib/queries';
import { PATHWAYS, SCENARIOS, SUB_PATHWAYS_C } from '../data/checklist';
import type { Pathway, Scenario, SubPathwayC } from '../data/types';

export function CreateChecklistPage() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useRequireApproved();
  const workspaceQ = useWorkspace(orgId);
  const create = useCreateChecklist();

  const [name, setName] = useState('');
  const [pathway, setPathway] = useState<Pathway | null>(null);
  const [subPathwayC, setSubPathwayC] = useState<SubPathwayC | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [err, setErr] = useState<string | null>(null);

  if (auth.loading) {
    return <CenterText>Loading…</CenterText>;
  }
  if (!auth.user) return null;

  const canSubmit =
    name.trim().length > 0 &&
    !!pathway &&
    (pathway !== 'C' || !!subPathwayC) &&
    scenarios.length > 0 &&
    !create.isPending;

  async function onSubmit() {
    if (!orgId || !pathway) return;
    setErr(null);
    try {
      const row = await create.mutateAsync({
        org_id: orgId,
        name: name.trim(),
        pathway,
        sub_pathway_c: pathway === 'C' ? subPathwayC : null,
        scenarios,
      });
      navigate(`/workspace/${orgId}/checklist/${row.id}`, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
      <Link
        to={`/workspace/${orgId}`}
        className="font-label text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:text-accent transition-colors inline-block mb-6"
      >
        ← back to workspace
      </Link>

      <div className="eyebrow mb-4">New checklist</div>
      <h1 className="editorial-h1 mb-3">
        Create a checklist for{' '}
        <em className="italic text-accent">
          {workspaceQ.data?.name ?? 'this workspace'}.
        </em>
      </h1>
      <p className="editorial-deck mb-12 max-w-2xl">
        Name it, pick your pathway and scenarios. The filtered checklist becomes
        editable on the next screen — every workspace member can check items off,
        and changes sync to everyone in the workspace.
      </p>

      {/* Name */}
      <section className="mb-12">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
          Name
        </div>
        <label className="block">
          <span className="font-editorial italic text-[14px] text-ink-muted mb-2 block">
            e.g. "FY26 CYBHI rollout" or "Spring 2026 onboarding cohort"
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            className="w-full bg-paper border border-rule px-4 py-3 font-editorial text-[17px] text-ink focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset"
            placeholder="Name your checklist"
          />
        </label>
      </section>

      {/* Pathway */}
      <section className="mb-12">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
          Pathway
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-rule border border-rule">
          {PATHWAYS.map((p) => {
            const selected = pathway === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setPathway(p.id);
                  if (p.id !== 'C') setSubPathwayC(null);
                }}
                className={
                  'bg-paper text-left p-5 transition-colors ' +
                  (selected ? 'ring-2 ring-accent ring-inset' : 'hover:bg-paper-deep')
                }
              >
                <div className="font-editorial italic text-xs text-accent mb-2">
                  Pathway {p.id}
                </div>
                <div className="font-editorial text-[18px] font-medium text-ink mb-2 leading-snug">
                  {p.name}
                </div>
                <div className="font-editorial text-[13px] text-ink-soft leading-relaxed">
                  {p.description}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Sub-pathway when C */}
      {pathway === 'C' && (
        <section className="mb-12">
          <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
            Pathway C — Sub-pathway
          </div>
          <div className="space-y-px bg-rule border border-rule">
            {SUB_PATHWAYS_C.map((sp) => {
              const selected = subPathwayC === sp.id;
              return (
                <button
                  key={sp.id}
                  type="button"
                  onClick={() => setSubPathwayC(sp.id)}
                  className={
                    'w-full bg-paper text-left p-5 transition-colors flex gap-5 items-start ' +
                    (selected ? 'ring-2 ring-accent ring-inset' : 'hover:bg-paper-deep')
                  }
                >
                  <div className="font-editorial italic text-sm text-accent shrink-0 w-10">
                    {sp.id}
                  </div>
                  <div>
                    <div className="font-editorial text-[16px] font-medium text-ink mb-1 leading-snug">
                      {sp.name}
                    </div>
                    <div className="font-editorial text-[13px] text-ink-soft leading-relaxed">
                      {sp.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Scenarios */}
      <section className="mb-12">
        <div className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent border-b border-rule pb-3 mb-5">
          Scenarios <span className="text-ink-muted font-normal italic font-editorial normal-case tracking-normal">(pick all that apply)</span>
        </div>
        <div className="grid md:grid-cols-2 gap-px bg-rule border border-rule">
          {SCENARIOS.map((s) => {
            const selected = scenarios.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() =>
                  setScenarios((prev) =>
                    prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id],
                  )
                }
                className={
                  'bg-paper text-left p-4 transition-colors flex gap-4 items-start ' +
                  (selected ? 'ring-2 ring-accent ring-inset' : 'hover:bg-paper-deep')
                }
              >
                <span
                  className="shrink-0 w-4 h-4 mt-1 border inline-block"
                  style={{
                    borderColor: selected ? 'var(--accent)' : '#807468',
                    background: selected ? 'var(--accent)' : 'transparent',
                  }}
                  aria-hidden="true"
                />
                <div className="flex-1">
                  <div className="font-editorial italic text-[11px] text-accent mb-0.5">
                    {s.id}
                  </div>
                  <div className="font-editorial text-[15px] font-medium text-ink leading-snug mb-1">
                    {s.name}
                  </div>
                  <div className="font-editorial text-[13px] text-ink-soft leading-relaxed">
                    {s.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {err && (
        <div className="border-l-2 border-[#a02525] pl-4 py-2 mb-6 font-editorial italic text-[14px] text-[#a02525]">
          {err}
        </div>
      )}

      <div className="border-t border-rule pt-8 flex items-center justify-between flex-wrap gap-4">
        <Link to={`/workspace/${orgId}`} className="btn-text" style={{ borderBottomColor: 'var(--rule)' }}>
          ← Cancel
        </Link>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="btn-text disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {create.isPending ? 'Creating…' : 'Create checklist →'}
        </button>
      </div>
    </div>
  );
}

function CenterText({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
      {children}
    </div>
  );
}
